import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import { loadBuildingsForSelector } from '../../../api';
import {
  CashFlowSummaryReportRow,
  incomeExpenseApi,
} from '../../../api/incomeExpenseApi';
import { useAuth } from '../../../contexts/AuthContext';
import {
  applyDefaultBuildingSelection,
  needsBuildingSelection,
  selectBuilding,
  shouldShowBuildingSelector,
} from '../../../utils/buildingSelection';
import BillingPeriodMonthPicker from '../../../components/common/BillingPeriodMonthPicker';
import { getCurrentBillingPeriodValue } from '../../../utils/billingPeriodPicker';
import {
  billingPeriodToFilenameKy,
  downloadExcel,
  sanitizeExportFilename,
} from '../../../utils/excelExport';

export interface IncomeExpenseCashFlowSummaryTabHandle {
  exportReport: () => Promise<void>;
}

const formatMoney = (value: number) => {
  const rounded = Math.round(value);
  if (rounded === 0) return '—';
  return new Intl.NumberFormat('vi-VN').format(rounded);
};

export const IncomeExpenseCashFlowSummaryTab = forwardRef<IncomeExpenseCashFlowSummaryTabHandle>(
  function IncomeExpenseCashFlowSummaryTab(_props, ref) {
    const { user } = useAuth();
    const [buildings, setBuildings] = useState<Array<{ id: string; name: string }>>([]);
    const [buildingId, setBuildingId] = useState('');
    const [collectionPeriod, setCollectionPeriod] = useState(getCurrentBillingPeriodValue());
    const [rows, setRows] = useState<CashFlowSummaryReportRow[]>([]);
    const [periodLabel, setPeriodLabel] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      loadBuildingsForSelector().then((data) => {
        const list = data || [];
        setBuildings(list);
        setBuildingId((prev) => applyDefaultBuildingSelection(list, user, prev));
      });
    }, [user]);

    const loadReport = useCallback(async () => {
      if (!buildingId || !collectionPeriod) return;
      setLoading(true);
      try {
        const data = await incomeExpenseApi.getCashFlowSummaryReport({
          buildingId,
          collectionPeriod,
        });
        setRows(data.rows || []);
        setPeriodLabel(data.currentPeriod || data.collectionPeriod || collectionPeriod);
      } catch {
        toast.error('Không thể tải tổng hợp tiền thu chi');
        setRows([]);
      } finally {
        setLoading(false);
      }
    }, [buildingId, collectionPeriod]);

    useEffect(() => {
      if (!buildingId) return;
      const timer = setTimeout(loadReport, 200);
      return () => clearTimeout(timer);
    }, [loadReport, buildingId]);

    const buildingName = useMemo(
      () => buildings.find((b) => b.id === buildingId)?.name || 'chung-cu',
      [buildings, buildingId],
    );

    const exportReport = useCallback(async () => {
      if (!rows.length) {
        toast.error('Không có dữ liệu để xuất');
        return;
      }
      const headers = [
        'Nội dung',
        'Số dư đầu kỳ',
        'Thu',
        'Chi',
        'Tồn cuối kỳ',
      ];
      const sheetRows = rows.map((row) => [
        row.label,
        row.openingBalance,
        row.income,
        row.expense,
        row.closingBalance,
      ]);
      const boldRowIndices = rows
        .map((row, index) => (row.kind === 'section' ? index : -1))
        .filter((index) => index >= 0);
      const metaRows: (string | number)[][] = [
        ['TỔNG HỢP TIỀN THU CHI'],
        ['Tòa nhà', buildingName],
        ['Kỳ báo cáo', periodLabel],
        [],
      ];
      const buildingPart = sanitizeExportFilename(buildingName) || 'chung-cu';
      const kyPart = billingPeriodToFilenameKy(periodLabel);
      const filename = sanitizeExportFilename(
        `tong-hop-tien-thu-chi_${buildingPart}_${kyPart}`,
      );
      downloadExcel('Báo cáo', headers, sheetRows, `${filename}.xlsx`, {
        metaRows,
        boldMetaRowIndices: [0],
        boldRowIndices,
        rightAlignColumnIndices: [1, 2, 3, 4],
      });
      toast.success('Đã xuất báo cáo');
    }, [rows, periodLabel, buildingName]);

    useImperativeHandle(ref, () => ({ exportReport }), [exportReport]);

    return (
      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex flex-wrap items-end gap-3">
            {shouldShowBuildingSelector(buildings) && (
              <select
                value={buildingId}
                onChange={(e) => selectBuilding(user?.id, e.target.value, setBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm min-w-[160px]"
              >
                {buildings.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            )}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-gray-500">Kỳ báo cáo</span>
              <BillingPeriodMonthPicker
                value={collectionPeriod}
                onChange={setCollectionPeriod}
              />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Nội dung
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase min-w-[120px]">
                  Số dư đầu kỳ
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase min-w-[100px]">
                  Thu
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase min-w-[100px]">
                  Chi
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase min-w-[120px]">
                  Tồn cuối kỳ
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                    Đang tải...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                    Chưa có dữ liệu
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row.id}
                    className={
                      row.kind === 'section'
                        ? 'bg-gray-100 font-semibold text-gray-900'
                        : 'hover:bg-gray-50/80 text-gray-700'
                    }
                  >
                    <td
                      className="px-4 py-2.5 text-sm border-b border-gray-100"
                      style={{ paddingLeft: `${16 + (row.depth ?? 0) * 20}px` }}
                    >
                      {row.label}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-right border-b border-gray-100 tabular-nums">
                      {formatMoney(row.openingBalance)}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-right border-b border-gray-100 tabular-nums">
                      {formatMoney(row.income)}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-right border-b border-gray-100 tabular-nums">
                      {formatMoney(row.expense)}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-right border-b border-gray-100 tabular-nums">
                      {formatMoney(row.closingBalance)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  },
);

export default IncomeExpenseCashFlowSummaryTab;
